import React, { useState, useEffect } from 'react';
import { auth } from '../config';
import { onAuthStateChanged } from 'firebase/auth';
import UserExcelViewer from './UserExcelViewer';
import HeaderMapper from '../utils/headerMapping';
import excelAwsS3Service from '../services/excelAwsS3Service';
import './UserExcelDashboard.css';

interface Task {
  id: string;
  title: string;
  description: string;
  status: string;
  priority: string;
  category: string;
  dueDate: string;
  assignedTo: string;
  assignedToName?: string;
  concernedPerson?: string;
  responsible?: string;
  createdBy: string;
  createdAt: string;
  taskType: 'individual' | 'team';
  collection: string;
}

interface AssignedTask {
  id: string;
  headers: string[];
  data: { [key: string]: any };
  sheetName: string;
  workbookId: string;
}

const UserExcelDashboard: React.FC = () => {
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [assignedTasks, setAssignedTasks] = useState<AssignedTask[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showExcelEditor, setShowExcelEditor] = useState(false);
  const [selectedTask, setSelectedTask] = useState<AssignedTask | null>(null);

  useEffect(() => {
    onAuthStateChanged(auth, (user) => {
      if (user) {
        setCurrentUser({
          id: user.uid,
          name: user.displayName || user.email || 'Unknown User',
          email: user.email || ''
        });
      }
    });
  }, []);

  useEffect(() => {
    if (currentUser) {
      fetchAssignedTasks();
    }
  }, [currentUser]);

  // Helper function to process workbook data from AWS S3
  const processWorkbookData = async (workbookData: any, tasks: AssignedTask[], workbookId?: string) => {
    if (workbookData.sheets && Array.isArray(workbookData.sheets)) {
      workbookData.sheets.forEach((sheet: any) => {
        console.log('📄 Processing sheet:', sheet.name, 'with data rows:', sheet.data?.length || 0);
        
        if (sheet.data && Array.isArray(sheet.data) && sheet.data.length > 1) {
          // Get headers from first row
          const headers = sheet.data[0].map((cell: any) => String(cell?.value || '').trim());
          console.log('📋 Sheet headers:', headers);
          console.log('📋 Sheet data sample:', sheet.data.slice(0, 3));
          console.log('📋 Total rows in sheet:', sheet.data.length);
          
          // Check each data row for assignments
          sheet.data.slice(1).forEach((row: any[], rowIndex: number) => {
            console.log(`🔍 Processing row ${rowIndex + 1}:`, row);
            const rowData: { [key: string]: any } = {};
            let isAssignedToUser = false;
            
            // Map row data to headers using standardized mapping
            const mappings = HeaderMapper.getAllColumnMappings(sheet.data[0]);
            console.log('🗺️ Column mappings:', mappings);
            
            // Extract ALL data from the row using actual headers (same as admin sees)
            row.forEach((cell: any, colIndex: number) => {
              const header = headers[colIndex];
              if (header) {
                rowData[header] = cell?.value || '';
                console.log(`📊 Row ${rowIndex + 1}, Col ${colIndex}: "${header}" = "${cell?.value || ''}"`);
              }
            });
            
            console.log(`📊 Complete row data for row ${rowIndex + 1}:`, rowData);

            // Check if this row is assigned to the current user using multiple methods
            // Method 1: Using standardized mapping
            const assignedToValue = mappings.assignedTo !== -1 ? row[mappings.assignedTo]?.value || '' : '';
            if (assignedToValue) {
              const assignedValueStr = assignedToValue.toString().toLowerCase();
              console.log('🔍 Checking assignment value:', assignedValueStr);
              console.log('🔍 Current user details for matching:', {
                id: currentUser.id?.toLowerCase(),
                name: currentUser.name?.toLowerCase(),
                email: currentUser.email?.toLowerCase(),
                displayName: currentUser.displayName?.toLowerCase()
              });
              
              if (
                assignedValueStr.includes(currentUser.id?.toLowerCase()) ||
                assignedValueStr.includes(currentUser.name?.toLowerCase()) ||
                assignedValueStr.includes(currentUser.email?.toLowerCase()) ||
                assignedValueStr.includes(currentUser.displayName?.toLowerCase())
              ) {
                isAssignedToUser = true;
                console.log('✅ Found assignment using standardized mapping');
                console.log('✅ Assignment match details:', {
                  assignedValue: assignedValueStr,
                  matchedField: 'standardized mapping',
                  rowIndex: rowIndex,
                  sheetName: sheet.name
                });
              }
            }
            
            // Method 2: Manual check for assignment fields (fallback)
            if (!isAssignedToUser) {
              row.forEach((cell: any, colIndex: number) => {
                const header = headers[colIndex];
                if (header) {
                  const headerLower = header.toLowerCase();
                  const cellValue = String(cell?.value || '').toLowerCase();
                  
                  // Check if this is an assignment field
                  if (
                    headerLower.includes('concerned person') ||
                    headerLower.includes('assigned to') ||
                    headerLower.includes('assigned') ||
                    headerLower.includes('responsible')
                  ) {
                    if (
                      cellValue.includes(currentUser.id?.toLowerCase()) ||
                      cellValue.includes(currentUser.name?.toLowerCase()) ||
                      cellValue.includes(currentUser.email?.toLowerCase()) ||
                      cellValue.includes(currentUser.displayName?.toLowerCase())
                    ) {
                      isAssignedToUser = true;
                      console.log('✅ Found assignment using manual check in field:', header);
                    }
                  }
                }
              });
            }
            
            if (isAssignedToUser) {
              console.log(`✅ Task assigned to user found in row ${rowIndex + 1}`);
              console.log(`📊 Original row data:`, rowData);
              console.log(`📊 Original headers:`, headers);
              
              // Show EXACT admin data with same headers - no mapping, no filtering
              const exactUserData: { [key: string]: any } = {};
              
              // Copy ALL data exactly as admin has it
              Object.keys(rowData).forEach(header => {
                exactUserData[header] = rowData[header];
              });

              console.log('📊 EXACT admin data for user:', {
                originalHeaders: headers,
                originalRowData: rowData,
                exactUserData: exactUserData,
                allHeaders: headers
              });

              // Use ALL headers from admin - don't filter anything
              tasks.push({
                id: `${workbookId || 'aws'}_${sheet.name}_${rowIndex + 1}`,
                headers: headers, // Use exact admin headers
                data: exactUserData, // Use exact admin data
                sheetName: sheet.name,
                workbookId: workbookId || 'aws'
              });
            }
          });
        }
      });
    }
  };

  const fetchAssignedTasks = async () => {
    try {
      setIsLoading(true);
      console.log('🔍 Fetching assigned tasks for user:', currentUser?.id);
      console.log('👤 Current user details:', currentUser);

      if (!currentUser?.id) {
        console.log('❌ No current user ID found');
        return;
      }

      const tasks: AssignedTask[] = [];
      
      // Fetch directly from AWS S3 - this is where the sheets are stored
      console.log('📊 Loading workbook from AWS S3...');
      try {
        // Load from AWS S3 using the folder ID from the logs
        const awsWorkbook = await excelAwsS3Service.loadWorkbook('Header checking task');
        if (awsWorkbook && awsWorkbook.sheets) {
          console.log('📊 Found workbook in AWS S3:', awsWorkbook);
          console.log('📊 AWS S3 workbook details:', {
            hasSheets: !!awsWorkbook.sheets,
            sheetsCount: awsWorkbook.sheets?.length || 0,
            sheetNames: awsWorkbook.sheets?.map((s: any) => s.name) || [],
            firstSheetData: awsWorkbook.sheets?.[0]?.data?.slice(0, 2) || 'No data'
          });
          
          // Process AWS S3 workbook data
          await processWorkbookData(awsWorkbook, tasks, 'Header checking task');
        } else {
          console.log('📊 No workbook found in AWS S3 or no sheets in workbook');
        }
      } catch (error) {
        console.log('📊 Error loading workbook from AWS S3:', error);
      }
      
      console.log('📋 Found assigned tasks:', tasks.length);
      setAssignedTasks(tasks);
      
      // Log detailed task information for debugging
      tasks.forEach((task, index) => {
        console.log(`📊 Task ${index + 1}:`, {
          id: task.id,
          sheetName: task.sheetName,
          headers: task.headers,
          data: task.data
        });
      });
      
    } catch (error) {
      console.error('❌ Error fetching assigned tasks:', error);
      console.error('❌ Error details:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Manual refresh function
  const handleRefresh = () => {
    console.log('🔄 Manual refresh triggered - fetching from AWS S3');
    fetchAssignedTasks();
  };

  const handleViewInExcel = (task: AssignedTask) => {
    console.log('📊 Opening Excel view for task:', task);
    console.log('📊 Task headers:', task.headers);
    console.log('📊 Task data:', task.data);
    
    // Use EXACT admin data - no modifications
    const userExcelTask = {
      ...task,
      headers: task.headers, // Use exact admin headers
      data: task.data // Use exact admin data
    };
    
    console.log('📊 User Excel task created with EXACT admin data:', userExcelTask);
    setSelectedTask(userExcelTask);
    setShowExcelEditor(true);
  };

  if (isLoading) {
    return (
      <div className="user-excel-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading your assigned tasks...</p>
        </div>
      </div>
    );
  }

      if (showExcelEditor && selectedTask) {
    return (
      <UserExcelViewer 
        task={selectedTask}
        onClose={() => setShowExcelEditor(false)}
      />
    );
  }

  return (
    <div className="user-excel-dashboard">
      <div className="dashboard-header">
        <div className="header-content">
          <div>
            <h1>📋 My Assigned Tasks</h1>
            <p>Tasks assigned to you from Excel workbooks</p>
          </div>
          <button 
            onClick={handleRefresh}
            className="refresh-button"
            title="Refresh tasks"
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      <div className="tasks-section">
        <h2>📊 Excel Assigned Tasks</h2>
        
        {assignedTasks.length === 0 ? (
          <div className="no-tasks">
            <p>No tasks assigned to you yet.</p>
            <p>Ask your admin to assign tasks in the Excel editor.</p>
          </div>
        ) : (
          <div className="consolidated-tasks-view">
            {(() => {
              // Consolidate all tasks into one view
              const allTaskRows = assignedTasks.flatMap(task => 
                Object.entries(task.data).map(([header, value]) => ({
                  header,
                  value: String(value || ''),
                  taskId: task.id,
                  sheetName: task.sheetName
                }))
              );
              
              // Group by common headers
              const commonHeaders = ['Particulars', 'Concerned Person', 'Status', 'Priority', 'Due Date'];
              const consolidatedData = assignedTasks.map(task => ({
                'Particulars': task.data['Particulars'] || task.data['Title'] || task.data['Name'] || '',
                'Concerned Person': task.data['Concerned Person'] || task.data['Assigned To'] || '',
                'Status': task.data['Status'] || '',
                'Priority': task.data['Priority'] || '',
                'Due Date': task.data['Due Date'] || task.data['Target Date'] || ''
              }));

              return (
                <div className="single-consolidated-task-card">
                  <div className="task-card-header">
                    <div className="task-title-section">
                      <h3>My Assigned Tasks</h3>
                      <div className="task-meta">
                        <span className="task-source">📊 Consolidated Excel Tasks</span>
                        <span className="task-id">#{assignedTasks.length} Tasks</span>
                      </div>
                    </div>
                    <div className="task-status-section">
                      <div className="status-badge status-mixed">
                        <div className="status-dot"></div>
                        <span>Multiple</span>
                      </div>
                      <div className="priority-badge priority-mixed">
                        <span>Mixed</span>
                      </div>
                    </div>
                  </div>

                  <div className="task-card-content">
                    <div className="excel-data-preview">
                      <div className="excel-preview-header">
                        <span className="preview-title">📋 Task Details</span>
                        <span className="preview-count">
                          {assignedTasks.length} task{assignedTasks.length > 1 ? 's' : ''}
                        </span>
                      </div>

                      <div className="consolidated-sheet-preview">
                        <div className="sheet-header">
                          <h3>📊 My Assigned Tasks</h3>
                          <p>All {assignedTasks.length} assigned tasks in one unified sheet</p>
                        </div>
                        
                        <div className="tasks-preview-list">
                          {consolidatedData.slice(0, 10).map((taskData, index) => (
                            <div key={index} className="task-preview-row">
                              <div className="task-row-number">#{index + 1}</div>
                              <div className="task-row-content">
                                <div className="task-row-title">
                                  {taskData['Particulars'] || `Task ${index + 1}`}
                                </div>
                                <div className="task-row-meta">
                                  <span className="task-status">{taskData['Status'] || 'TO-DO'}</span>
                                  <span className="task-priority">{taskData['Priority'] || 'Medium'}</span>
                                  <span className="task-assigned">{taskData['Concerned Person'] || 'Unassigned'}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {assignedTasks.length > 10 && (
                          <div className="more-tasks-preview">
                            <div className="more-tasks-count">
                              +{assignedTasks.length - 10} More Tasks
                            </div>
                            <div className="more-tasks-note">
                              All tasks will be available in the Excel editor
                            </div>
                          </div>
                        )}

                        <div className="sheet-actions">
                          <button
                            onClick={() => {
                              // Create a consolidated task for Excel editor
                              const consolidatedTask = {
                                id: 'all_assigned_tasks',
                                headers: commonHeaders,
                                data: consolidatedData,
                                sheetName: 'My Assigned Tasks',
                                workbookId: 'consolidated',
                                workbookName: `My Assigned Tasks - ${assignedTasks.length} Task${assignedTasks.length > 1 ? 's' : ''}`,
                                totalTasks: assignedTasks.length,
                                taskRows: consolidatedData,
                                isFromDirectNotification: true
                              };
                              handleViewInExcel(consolidatedTask);
                            }}
                            className="open-excel-editor-btn"
                          >
                            📊 Open Full Excel Editor
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>

      <div className="dashboard-footer">
        <button 
          onClick={fetchAssignedTasks}
          className="btn-refresh"
        >
          🔄 Refresh Tasks
        </button>
      </div>
    </div>
  );
};

export default UserExcelDashboard;



